import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Directive, Inject, Input, OnDestroy, NgZone, Renderer2 } from '@angular/core';
import { AxisUtilsService, ModalService, VERSION } from 'axis-components';
import { FnolIdleTimeoutModalComponent } from 'src/app/features/fnol/cdp-lite/cdp-lite/fnol-lite/fnol-lite/components/fnol-idle-timeout-modal/fnol-idle-timeout-modal.component';

@Directive({
  selector: '[cdp-axis-accessibility]',
})
export class CdpAxisAccessibilityDirective implements AfterViewInit, OnDestroy {
  @Input() enableIdleTimer = false;
  @Input() idleConfig: any;

  idleTimer: any;
  private isModalOpen = false;
  private isHiddenCountdown = false;
  private isSessionExpired = false;
  private idleDeadlineAt = 0;
  private warningDeadlineAt = 0;
  private readonly unlisteners: Array<() => void> = [];
  private readonly handleVisibilityChange = () => {
    if (this.documentRef.hidden) {
      console.log('[IdleTimer] Tab/page hidden. Starting hidden countdown flow.');
      this.startHiddenCountdown();
    } else {
      console.log('[IdleTimer] Tab/page visible again. Restoring active timeout flow.');
      this.restoreVisibleTimer();
    }
  };
  private readonly handleWindowFocus = () => {
    if (this.documentRef.hidden || this.isHiddenCountdown) {
      console.log('[IdleTimer] Focus ignored while tab is hidden.');
      return;
    }

    console.log('[IdleTimer] Focus returned. Reconciling idle timer against absolute deadlines.');
    this.reconcileIdleTimer();
  };
  private readonly handleClick = (event: MouseEvent) => {
    this.resetTimersEvent();
    if (event.detail !== 0) {
      this.removeBodyClass('axis-keyboard');
    }
  };
  private readonly handleKeyup = () => {
    this.resetTimersEvent();
    this.removeBodyClass('axis-touch');
    this.addBodyClass('axis-keyboard');
  };
  private readonly handleMousemove = () => {
    this.resetTimersEvent();
    this.removeBodyClass('axis-keyboard');
  };
  private readonly handleTouchstart = () => {
    this.resetTimersEvent();
    this.removeBodyClass('axis-keyboard');
    this.addBodyClass('axis-touch');
  };

  constructor(
    private zone: NgZone,
    private renderer: Renderer2,
    private modalService: ModalService,
    private axisService: AxisUtilsService,
    @Inject(DOCUMENT) private documentRef: Document
  ) {
    this.axisService.close.subscribe(res => {
      this.isModalOpen = false;

      if (res?.buttonType === 'primary' && !res?.autoLoggedOut) {
        console.log('[IdleTimer] Continue session selected. Restarting idle timer.');
        this.startIdleTimer();
      }
    });
  }

  ngAfterViewInit(): void {
    this.renderer.setAttribute(this.documentRef.body, 'axis-version', VERSION.version);
    this.zone.runOutsideAngular(() => {
      if (this.enableIdleTimer) {
        this.startIdleTimer();
      }
      this.bindGlobalListeners();
    });
  }

  ngOnDestroy(): void {
    this.clearIdleTimer();
    this.unlisteners.splice(0).forEach(unlisten => unlisten());
  }

  startIdleTimer(): void {
    if (this.canRunIdleTimer() && !this.isSessionExpired) {
      this.modalService.destroyModalComponent();
      this.idleDeadlineAt = Date.now() + this.getIdleTimeoutMs();
      this.warningDeadlineAt = this.idleDeadlineAt + this.getWarningTimeoutMs();
      this.scheduleIdleTimeout(this.getIdleTimeoutMs());
    }
  }

  resetTimersEvent(): void {
    if (this.canRunIdleTimer()) {
      this.startIdleTimer();
    }
  }

  openTimeOutModal(_type: any, timeoutSecs = Number(this.idleConfig?.timeoutSecs) || 0): void {
    if (this.isModalOpen) {
      return;
    }

    this.modalService.createModalComponent(FnolIdleTimeoutModalComponent, {
      ...this.idleConfig,
      timeoutSecs,
    });
    this.isModalOpen = true;
  }

  private startHiddenCountdown(): void {
    console.log('[IdleTimer] Hidden flow entered. Pausing timer until the tab is visible.');
    if (!this.canRunIdleTimer() || !this.idleDeadlineAt) {
      console.log('[IdleTimer] Hidden flow skipped because idle timer is disabled or no deadline exists.');
      return;
    }

    this.clearIdleTimer();
    this.isHiddenCountdown = true;
    console.log('[IdleTimer] Hidden flow complete. No modal opened while the tab is hidden.');
  }

  private restoreVisibleTimer(): void {
    console.log('[IdleTimer] Restore flow entered after tab became visible.');
    if (!this.isHiddenCountdown) {
      console.log('[IdleTimer] No hidden countdown active. Reconcile active idle timer instead.');
      this.reconcileIdleTimer();
      return;
    }

    console.log('[IdleTimer] Hidden countdown was active. Cleaning up hidden state.');
    this.isHiddenCountdown = false;

    if (this.isModalOpen) {
      console.log('[IdleTimer] Closing existing timeout modal before restoring visible flow.');
      this.dismissTimeoutModal();
    }

    const now = Date.now();
    if (now >= this.warningDeadlineAt) {
      console.log('[IdleTimer] Inactivity exceeded idleTime + timeoutSecs. Triggering close event.');
      this.triggerCloseEvent();
      return;
    }

    if (now >= this.idleDeadlineAt) {
      const remainingWarningSecs = Math.ceil((this.warningDeadlineAt - now) / 1000);
      console.log('[IdleTimer] Inactivity is in warning period. Opening modal with remaining seconds:', remainingWarningSecs);
      this.openTimeOutModal('idle', remainingWarningSecs);
      return;
    }

    const remainingIdleMs = this.idleDeadlineAt - now;
    console.log('[IdleTimer] Inactivity is below idleTime. Resuming idle timer:', Math.ceil(remainingIdleMs / 1000), 'seconds');
    this.scheduleIdleTimeout(remainingIdleMs);
  }

  private reconcileIdleTimer(): void {
    if (this.documentRef.hidden || this.isHiddenCountdown) {
      console.log('[IdleTimer] Reconcile skipped while tab is hidden; no modal will be opened.');
      return;
    }

    if (!this.canRunIdleTimer() || !this.idleDeadlineAt) {
      console.log('[IdleTimer] Reconcile skipped because timer cannot run or deadline is not set.');
      return;
    }

    const remainingTime = this.idleDeadlineAt - Date.now();
    console.log('[IdleTimer] Reconcile remaining idle time =', Math.ceil(remainingTime / 1000), 'seconds');

    if (Date.now() >= this.warningDeadlineAt) {
      console.log('[IdleTimer] Inactivity window already expired. Triggering close event.');
      this.triggerCloseEvent();
      return;
    }

    if (remainingTime <= 0) {
      const remainingWarningSecs = Math.ceil((this.warningDeadlineAt - Date.now()) / 1000);
      console.log('[IdleTimer] Idle deadline reached. Opening modal with remaining warning seconds.', remainingWarningSecs);
      this.openTimeOutModal('idle', remainingWarningSecs);
      return;
    }

    this.scheduleIdleTimeout(remainingTime);
    console.log('[IdleTimer] Reconcile complete: idle timer rescheduled.');
  }

  private getIdleTimeoutMs(): number {
    return (Number(this.idleConfig?.idleTime) || 0) * 1000;
  }

  private getWarningTimeoutMs(): number {
    return (Number(this.idleConfig?.timeoutSecs) || 0) * 1000;
  }

  private canRunIdleTimer(): boolean {
    return this.enableIdleTimer && !this.isModalOpen;
  }

  private bindGlobalListeners(): void {
    this.unlisteners.push(
      this.renderer.listen('document', 'visibilitychange', this.handleVisibilityChange),
      this.renderer.listen('window', 'focus', this.handleWindowFocus),
      this.renderer.listen('document', 'click', this.handleClick),
      this.renderer.listen('document', 'keyup', this.handleKeyup),
      this.renderer.listen('document', 'mousemove', this.handleMousemove),
      this.renderer.listen('document', 'touchstart', this.handleTouchstart)
    );
  }

  private scheduleIdleTimeout(timeoutMs: number): void {
    this.clearIdleTimer();
    this.idleTimer = setTimeout(() => {
      if (this.documentRef.hidden) {
        console.log('[IdleTimer] Idle deadline reached while hidden. Waiting for tab visibility to continue flow.');
        this.isHiddenCountdown = true;
        return;
      }

      console.log('[IdleTimer] Idle timer expired. Opening timeout modal.');
      this.openTimeOutModal('idle');
    }, timeoutMs);
  }

  private triggerCloseEvent(): void {
    if (this.isSessionExpired) {
      return;
    }

    this.isSessionExpired = true;
    this.clearIdleTimer();
    this.isHiddenCountdown = false;
    this.dismissTimeoutModal();
    this.axisService.close.next({ buttonType: 'secondary', autoLoggedOut: true });
  }

  private dismissTimeoutModal(): void {
    this.clearIdleTimer();
    this.isModalOpen = false;
    this.modalService.destroyModalComponent();
  }

  private clearIdleTimer(): void {
    clearTimeout(this.idleTimer);
  }

  private removeBodyClass(className: string): void {
    this.renderer.removeClass(this.documentRef.body, className);
  }

  private addBodyClass(className: string): void {
    this.renderer.addClass(this.documentRef.body, className);
  }
}